/**
 * A titled block of a page.
 *
 * Card titles came in three forms — `<h2>` in a card head, `<strong>` in a
 * card head, a bare `<h3>` — and the global `h2` rule rendered one of them as
 * a 13px uppercase table header while `.card > h2` rendered another at 15px.
 * This is the one form. The page title lives in the topbar and is rendered
 * once; a section title is the next level down and looks like it.
 */

import type React from 'react';

export function Section({
  title,
  summary,
  actions,
  flush = false,
  children,
}: {
  title?: React.ReactNode;
  /** One line of context. Not a paragraph. */
  summary?: React.ReactNode;
  actions?: React.ReactNode;
  /** No inner padding — for a section whose whole body is a table. */
  flush?: boolean;
  children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className={flush ? 'card card-flush' : 'card'}>
      {(title !== undefined || actions !== undefined) && (
        <div className="section-head">
          <div>
            {title !== undefined && <h2 className="section-title">{title}</h2>}
            {summary !== undefined && <div className="small muted">{summary}</div>}
          </div>
          {actions !== undefined && <div className="section-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** The row above a table or list: search, filters, the page action. */
export function Toolbar({
  children,
  end,
}: {
  children?: React.ReactNode;
  /** Right-aligned: the one primary action, usually. */
  end?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="toolbar">
      <div className="toolbar-start">{children}</div>
      {end !== undefined && <div className="toolbar-end">{end}</div>}
    </div>
  );
}

/** A filter that is on or off. */
export function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={active ? 'chip active' : 'chip'}
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
